import React from "react";
import { drawerClose, drawerOpen, drawerUpdate } from "../../../library/rxjsSubjects";
import DrawerComponent from "./drawer";

class DrawerHoc extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      drawerStack: [],
    };

    // Bind methods to preserve 'this' context
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.closeAllDrawers = this.closeAllDrawers.bind(this);
    this.updateDrawer = this.updateDrawer.bind(this);
    this.getCurrentDrawerData = this.getCurrentDrawerData.bind(this);
  }

  componentDidMount() {
    try {
      this.drawerOpenSubscription = drawerOpen.subscribe((...params) => this.openDrawer(...params));
      this.drawerCloseSubscription = drawerClose.subscribe((...params) =>
        this.closeDrawer(...params)
      );
      this.drawerUpdateSubscription = drawerUpdate.subscribe(({ props, forceRemount }) =>
        this.updateDrawer(props, forceRemount)
      );
    } catch (error) {
      console.error("Error subscribing to drawer events:", error);
    }
  }

  componentWillUnmount() {
    if (this.drawerOpenSubscription) {
      this.drawerOpenSubscription.unsubscribe();
    }
    if (this.drawerCloseSubscription) {
      this.drawerCloseSubscription.unsubscribe();
    }
    if (this.drawerUpdateSubscription) {
      this.drawerUpdateSubscription.unsubscribe();
    }
  }

  getCurrentDrawerData() {
    const { drawerStack } = this.state;
    return {
      isOpen: Boolean(drawerStack.length),
      currentDrawer: drawerStack[drawerStack.length - 1] || {},
    };
  }

  openDrawer(drawer) {
    try {
      const drawerName = drawer[0] || "";
      const drawerProps = drawer[1] || {}; // Props for Ant Design Drawer component
      const componentProps = drawer[2] || {}; // Props for the component inside drawer

      const newDrawer = {
        type: drawerName,
        drawerProps: drawerProps, // For Drawer component (width, placement, etc.)
        componentProps: componentProps, // For inner component
        mountKey: Date.now(),
        isOpen: true,
      };

      this.setState(prevState => ({
        drawerStack: [...prevState.drawerStack, newDrawer],
      }));
    } catch (error) {
      console.error("Error opening drawer:", error);
    }
  }

  closeDrawer(drawerName) {
    this.setState(prevState => {
      const newStack = [...prevState.drawerStack];

      if (drawerName) {
        // Remove specific drawer by name
        const indexToRemove = newStack.findIndex(drawer => drawer.type === drawerName);
        if (indexToRemove !== -1) {
          newStack.splice(indexToRemove, 1);
        }
      } else {
        // Remove last drawer (default behavior)
        newStack.pop();
      }

      return {
        drawerStack: newStack,
      };
    });
  }

  closeAllDrawers() {
    this.setState({
      drawerStack: [],
    });
  }

  updateDrawer(newComponentProps, forceRemount = false) {
    this.setState(prevState => {
      const newStack = [...prevState.drawerStack];
      if (newStack.length > 0) {
        // Update the component props of the current (topmost) drawer
        const currentDrawer = newStack[newStack.length - 1];
        newStack[newStack.length - 1] = {
          ...currentDrawer,
          componentProps: {
            ...currentDrawer.componentProps,
            ...newComponentProps,
          },
          // Only update mountKey if forceRemount is explicitly requested
          ...(forceRemount && { mountKey: Date.now() }),
        };
      }
      return {
        drawerStack: newStack,
      };
    });
  }

  render() {
    try {
      const { currentDrawer, isOpen } = this.getCurrentDrawerData();

      if (!isOpen) return null;

      return (
        <DrawerComponent
          open
          type={currentDrawer.type || ""}
          drawerProps={currentDrawer.drawerProps || {}}
          componentProps={currentDrawer.componentProps || {}}
          mountKey={currentDrawer.mountKey}
          handleClose={this.closeDrawer}
          openDrawerHandler={this.openDrawer}
          updateDrawerHandler={this.updateDrawer}
          closeAllDrawers={this.closeAllDrawers}
        />
      );
    } catch (error) {
      console.error("Error rendering drawer:", error);
      return null;
    }
  }
}

export default DrawerHoc;
